import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Briefcase,
  Plus,
  Trash2,
  Key,
  Globe,
  Layout,
  ArrowRight,
  ShieldCheck,
  Check,
  Copy,
  PlusCircle,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  RefreshCw,
  UserPlus,
  Mail,
  Code2,
  MousePointer2,
  Eye,
  Search,
  Building2,
  X,
  FileCode,
  Smartphone
} from 'lucide-react';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const Skeleton = ({ className = "" }) => (
  <div className={`relative overflow-hidden bg-white/5 rounded-2xl ${className}`}>
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent"
      animate={{
        x: ['-100%', '100%'],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  </div>
);

const WorkspaceSwitcherSkeleton = () => (
  <div className="flex items-center gap-3 overflow-x-auto pb-4 scrollbar-hide">
    {[1, 2, 3].map(i => (
      <div key={i} className="flex items-center gap-3 px-6 py-4 rounded-2xl border border-white/5 bg-white/5 min-w-[180px]">
        <Skeleton className="w-8 h-8 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-2 w-12" />
        </div>
      </div>
    ))}
  </div>
);

const ProjectCardSkeleton = () => (
  <div className="p-6 rounded-2xl border border-white/5 bg-white/5 space-y-4">
    <div className="flex items-center gap-4">
      <Skeleton className="w-12 h-12 rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  </div>
);

const ProjectDetailSkeleton = () => (
  <div className="space-y-8 animate-pulse">
    <div className="glass-card p-8 border-white/5 bg-white/5 min-h-[160px] flex flex-col justify-between relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="space-y-4 relative z-10">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-4 w-56" />
      </div>
    </div>
    <div className="space-y-8 mt-12">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-64" />
      </div>
      <div className="glass-panel p-1 rounded-[1rem] bg-white/5 border border-white/10 flex gap-1">
        <Skeleton className="h-12 flex-1 rounded-2xl" />
        <Skeleton className="h-12 flex-1 rounded-2xl" />
        <Skeleton className="h-12 flex-1 rounded-2xl" />
      </div>
      <div className="grid grid-cols-1 gap-6">
        <div className="glass-card p-10 border-white/5 bg-white/5 space-y-6">
          <div className="space-y-3">
            <Skeleton className="h-4 w-full max-w-md" />
            <Skeleton className="h-16 w-full rounded-2xl" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full max-w-sm" />
            <Skeleton className="h-16 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProjectCard = ({ project, onSelect, active, t, userEmail }) => {
  const getRole = () => {
    const ownerEmail = project.ownerEmail || project.owner_email;
    if (ownerEmail?.toLowerCase() === userEmail?.toLowerCase()) return 'pm';
    const member = project.members?.find(m => m.email?.toLowerCase() === userEmail?.toLowerCase());
    return member?.role || 'dev';
  };
  const role = getRole();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onSelect(project)}
      className={`p-6 rounded-2xl border transition-all cursor-pointer group relative overflow-hidden glass-panel ${active ? 'border-emerald-500/40' : 'border-white/5 hover:border-white/10 hover:bg-white/5'
        }`}
    >
      {active && (
        <motion.div
          layoutId="activeProjectIndicator"
          className="absolute inset-0 bg-emerald-500/5 shadow-[0_0_30px_rgba(16,185,129,0.15)] z-0"
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${active ? 'bg-emerald-500 text-white shadow-lg' : 'bg-white/5 text-slate-400 group-hover:bg-white/10'
            }`}>
            <Briefcase size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-100">{project.name}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
              {t(role.toLowerCase().startsWith('role_') ? role.toLowerCase() : `role_${role.toLowerCase()}`)}
            </p>
          </div>
        </div>
        <AnimatePresence>
          {active && (
            <motion.div
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -10, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArrowRight className="text-emerald-500" size={20} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute top-0 right-0 p-2 z-10"
          >
            <div className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-emerald-500/20 text-emerald-500 border border-emerald-500/30">
              {t('active')}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const CopyButton = ({ text, className = "", activeColor = "text-emerald-500" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-2 rounded-lg transition-all relative ${className}`}
    >
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div
            key="check"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={activeColor}
          >
            <Check size={18} />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Copy size={18} />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
};

const OriginsModal = ({ isOpen, onClose, keyData, onUpdate, isUpdating, t }) => {
  const [origins, setOrigins] = useState(keyData.allowedOrigins || []);
  const [newOrigin, setNewOrigin] = useState('');

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newOrigin) return;
    if (!origins.includes(newOrigin)) {
      setOrigins([...origins, newOrigin]);
    }
    setNewOrigin('');
  };

  const handleRemove = (origin) => {
    setOrigins(origins.filter(o => o !== origin));
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="relative bg-slate-900 w-full max-w-lg rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl">
        <div className="p-8 border-b border-white/5 bg-white/[0.02]">
          <div className="flex items-center justify-between mb-2">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
              <Globe size={24} />
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-all">
              <X size={20} />
            </button>
          </div>
          <h2 className="text-2xl font-black text-slate-100">{t('manageOrigins')}</h2>
          <p className="text-slate-500 text-sm mt-1">{t('originsDesc')}</p>
        </div>

        <div className="p-8 space-y-6">
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2 ml-1">{t('addOrigin')}</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="https://example.com"
                  className="flex-1 bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-slate-100 focus:outline-none focus:border-emerald-500/50 transition-all font-medium"
                  value={newOrigin}
                  onChange={(e) => setNewOrigin(e.target.value)}
                />
                <button type="submit" className="p-4 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition-all active:scale-95">
                  <Plus size={20} />
                </button>
              </div>
            </div>
          </form>

          <div className="space-y-2 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-white/10">
            {origins.length === 0 ? (
              <p className="text-center py-8 text-slate-600 italic text-sm">{t('noOrigins')}</p>
            ) : (
              origins.map(origin => (
                <div key={origin} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/5 group">
                  <span className="text-sm font-medium text-slate-300">{origin}</span>
                  <button onClick={() => handleRemove(origin)} className="p-2 text-slate-600 hover:text-rose-500 transition-all">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/5">
            <button onClick={onClose} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">
              {t('cancel')}
            </button>
            <button
              onClick={() => onUpdate(keyData._id, origins)}
              disabled={isUpdating}
              className="flex-1 py-4 bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-emerald-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              {isUpdating ? <RefreshCw className="animate-spin" size={16} /> : <Check size={16} />}
              {t('save')}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const Projects = () => {
  const { t, language } = useLanguage();
  const { user, setUser } = useAuth();
  const { addToast } = useToast();
  const {
    setActiveProjectId: setContextActiveProjectId,
    projects,
    filteredProjects,
    activeProjectId,
    workspaces,
    activeWorkspaceId,
    setActiveWorkspaceId,
    userRole,
    loading: isLoadingProjects,
    isInitialized,
    refreshProjects
  } = useProject();

  const activeProject = projects.find(p => p._id === activeProjectId);

  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);


  const [newProjectName, setNewProjectName] = useState('');

  const [keys, setKeys] = useState([]);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyOrigin, setNewKeyOrigin] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showDeleteKeyModal, setShowDeleteKeyModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isDeletingKey, setIsDeletingKey] = useState(false);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('dev');
  const [isInviting, setIsInviting] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  // Calculate current PM count (Owner + Members + Pending Invitations)
  const pmCountTotal = useMemo(() => {
    if (!activeProject) return 0;
    const pmInMembers = activeProject.members?.filter(m => m.role === 'pm').length || 0;
    const pmInPending = (pendingInvitations || []).filter(i => i.role === 'pm').length || 0;
    return 1 + pmInMembers + pmInPending; // 1 for the owner
  }, [activeProject, pendingInvitations]);

  const [errorModal, setErrorModal] = useState({ show: false, title: '', message: '' });
  const [successModal, setSuccessModal] = useState({ show: false, title: '', message: '' });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [memberToPromote, setMemberToPromote] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferConfirmationInput, setTransferConfirmationInput] = useState('');
  const [isGeneratingPersonalKey, setIsGeneratingPersonalKey] = useState(false);
  const [isGeneratingWeeklyKey, setIsGeneratingWeeklyKey] = useState(false);
  const [weeklyKey, setWeeklyKey] = useState(null);

  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  const [showCancelInviteModal, setShowCancelInviteModal] = useState(false);
  const [inviteToCancel, setInviteToCancel] = useState(null);
  const [isCancellingInvite, setIsCancellingInvite] = useState(false);
  const [activeAccessTab, setActiveAccessTab] = useState('api');

  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);
  const [workspaceDeleteInput, setWorkspaceDeleteInput] = useState('');
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [isOriginsModalOpen, setIsOriginsModalOpen] = useState(false);
  const [selectedKeyForOrigins, setSelectedKeyForOrigins] = useState(null);
  const [isUpdatingOrigins, setIsUpdatingOrigins] = useState(false);

  // Modal Scroll Lock Logic
  useEffect(() => {
    const isAnyModalOpen = [
      showCreateModal,
      showKeyModal,
      isInviteModalOpen,
      showDeleteModal,
      showRemoveMemberModal,
      showCancelInviteModal,
      showCreateWorkspaceModal,
      showDeleteWorkspaceModal,
      showTransferModal,
      showDeleteKeyModal,
      isOriginsModalOpen,
      successModal.show,
      errorModal.show,
      generatedKey
    ].some(Boolean);

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [
    showCreateModal, showKeyModal, isInviteModalOpen, showDeleteModal,
    showRemoveMemberModal, showCancelInviteModal, showCreateWorkspaceModal,
    showDeleteWorkspaceModal, showTransferModal, showDeleteKeyModal,
    isOriginsModalOpen, successModal.show, errorModal.show, generatedKey
  ]);


  const fetchKeys = async (id) => {
    try {
      const res = await api.get(`/teams/${id}/keys`);
      setKeys(res.data);
    } catch (err) {
      // Failed to fetch keys
    }
  };

  const fetchInvitations = async (id) => {
    try {
      const res = await api.get(`/projects/${id}/invitations`);
      setPendingInvitations(res.data);
    } catch (err) {
      setPendingInvitations([]);
    }
  };

  useEffect(() => {
    refreshProjects();
  }, []);

  const calculateDaysLeft = (expiryDate) => {
    if (!expiryDate) return 0;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  useEffect(() => {
    if (activeProjectId) {
      setWeeklyKey(null);
      fetchKeys(activeProjectId);
      if (userRole === 'pm') {
        fetchInvitations(activeProjectId);
      } else {
        setPendingInvitations([]);
      }

      if (activeProject && activeProject.currentUserWeeklyKey && activeProject.currentUserWeeklyKeyExpiresAt) {
        if (new Date(activeProject.currentUserWeeklyKeyExpiresAt) > new Date()) {
          setWeeklyKey(activeProject.currentUserWeeklyKey);
        } else {
          setWeeklyKey(null);
        }
      } else {
        setWeeklyKey(null);
      }
    } else {
      setKeys([]);
      setPendingInvitations([]);
      setWeeklyKey(null);
    }
  }, [activeProjectId, userRole, activeProject]);

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/workspaces', { name: newWorkspaceName });
      await refreshProjects();
      setWorkspaces([...workspaces, res.data]);
      setActiveWorkspaceId(res.data._id);
      addToast(t('workspaceCreated'), 'success');
      setShowCreateWorkspaceModal(false);
      setNewWorkspaceName('');
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: 'Failed to create workspace'
      });
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceToDelete) return;
    setIsDeletingWorkspace(true);
    try {
      await api.delete(`/workspaces/${workspaceToDelete._id}`);
      const updatedWorkspaces = workspaces.filter(w => w._id !== workspaceToDelete._id);
      setWorkspaces(updatedWorkspaces);
      
      if (activeWorkspaceId === workspaceToDelete._id) {
        setActiveWorkspaceId(updatedWorkspaces.length > 0 ? updatedWorkspaces[0]._id : null);
      }
      
      setShowDeleteWorkspaceModal(false);
      setWorkspaceToDelete(null);
      setWorkspaceDeleteInput('');
      refreshProjects();
      setSuccessModal({
        show: true,
        title: t('success'),
        message: 'Workspace deleted successfully'
      });
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to delete workspace'
      });
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    // Check Free Tier Limit (1 Project)
    const selectedWorkspace = workspaces.find(w => w._id === activeWorkspaceId);
    const isFounder = selectedWorkspace?.isFoundersTeam;
    if (selectedWorkspace?.subscription?.tier === 'free' && !isFounder) {
      const workspaceProjectCount = projects.filter(p => p.workspaceId === activeWorkspaceId).length;
      if (workspaceProjectCount >= 1) {
        setErrorModal({
          show: true,
          title: t('tier_limit_reached'),
          message: 'Free tier workspaces are limited to 1 project. Upgrade to PRO to create more.'
        });
        return;
      }
    }

    try {
      const res = await api.post('/teams', { 
        name: newProjectName,
        workspaceId: activeWorkspaceId 
      });
      await refreshProjects();
      setContextActiveProjectId(res.data._id);
      addToast(t('projectCreated'), 'success');
      setShowCreateModal(false);
      setNewProjectName('');
    } catch (err) {
      const apiError = err.response?.data?.error || '';
      setErrorModal({
        show: true,
        title: t('error'),
        message: apiError === 'TIER_LIMIT_REACHED' 
          ? err.response?.data?.message 
          : t('errorCreatingProject')
      });
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/teams/${activeProjectId}/keys`, {
        name: newKeyName,
        type: 'team',
        origins: [newKeyOrigin]
      });
      setGeneratedKey(res.data.apiKey);
      setKeys([...keys, res.data.data]);
      setNewKeyName('');
      setNewKeyOrigin('');
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('keyError'),
        message: t('errorGeneratingKey')
      });
    }
  };

  const handleDeleteKey = async () => {
    if (!keyToDelete || !activeProjectId) return;
    setIsDeletingKey(true);
    try {
      await api.delete(`/teams/${activeProjectId}/keys/${keyToDelete._id}`);
      setKeys(keys.filter(k => k._id !== keyToDelete._id));
      setShowDeleteKeyModal(false);
      setKeyToDelete(null);
    } catch (err) {
      const apiError = err.response?.data?.error || '';
      setErrorModal({
        show: true,
        title: t('keyError'),
        message: apiError === 'CANNOT_DELETE_LAST_KEY' ? t('cannotDeleteLastKey') : t('errorDeletingKey')
      });
    } finally {
      setIsDeletingKey(false);
    }
  };

  const handleUpdateOrigins = async (keyId, origins) => {
    setIsUpdatingOrigins(true);
    try {
      await api.patch(`/teams/${activeProjectId}/keys/${keyId}`, {
        allowedOrigins: origins
      });
      setKeys(keys.map(k => k._id === keyId ? { ...k, allowedOrigins: origins } : k));
      setIsOriginsModalOpen(false);
      addToast(t('originsUpdated'), 'success');
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to update origins'
      });
    } finally {
      setIsUpdatingOrigins(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setIsInviting(true);
    try {
      await api.post(`/projects/${activeProjectId}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      setSuccessModal({
        show: true,
        title: t('invitationSent'),
        message: t('invitationSentDesc').replace('{{email}}', inviteEmail)
      });
      setInviteEmail('');
      setInviteRole('dev');
      setIsInviteModalOpen(false);
      fetchInvitations(activeProjectId);
      refreshProjects();
    } catch (err) {
      const apiError = err.response?.data?.error || '';
      const apiMessage = err.response?.data?.message || '';
      const lowerError = apiError.toLowerCase();
      
      const modalTitle = (t(lowerError) !== lowerError) ? t(lowerError) : t('invitationError');

      setErrorModal({
        show: true,
        title: modalTitle,
        message: lowerError.includes('already a member') 
          ? t('alreadyMember') 
          : (apiMessage || t('errorInvitingMember'))
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleGeneratePersonalKey = async () => {
    setIsGeneratingPersonalKey(true);
    try {
      const res = await api.post('/users/me/personal-key');
      setUser({ ...user, individualApiKey: res.data.personalKey });
      setSuccessModal({
        show: true,
        title: t('personalKeyGenerated'),
        message: t('personalKeyDesc')
      });
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to generate personal key'
      });
    } finally {
      setIsGeneratingPersonalKey(false);
    }
  };

  const handleGenerateWeeklyKey = async () => {
    if (!activeProjectId) return;
    setIsGeneratingWeeklyKey(true);
    try {
      const url = `/projects/${activeProjectId}/weekly-key${weeklyKey ? '?force=true' : ''}`;
      const res = await api.post(url);
      if (res.data.weeklyKey) {
        setWeeklyKey(res.data.weeklyKey);
        // Sync context immediately
        refreshProjects();
      }
      setSuccessModal({
        show: true,
        title: t('weeklyKeyGenerated'),
        message: t('weeklyKeyDesc')
      });
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to generate weekly key'
      });
    } finally {
      setIsGeneratingWeeklyKey(false);
    }
  };

  const handleProjectSelect = (project) => {
    setContextActiveProjectId(project._id);
  };

  const handleDeleteAttempt = (project) => {
    setProjectToDelete(project);
    setDeleteConfirmationInput('');
    setShowDeleteModal(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      await api.delete(`/teams/${projectToDelete._id}`);
      if (activeProjectId === projectToDelete._id) {
        const other = projects.find(p => p._id !== projectToDelete._id);
        if (other) setContextActiveProjectId(other._id);
        else setContextActiveProjectId(null);
      }
      setShowDeleteModal(false);
      setProjectToDelete(null);
      refreshProjects();
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('deletionError'),
        message: t('errorDeletingProject')
      });
    }
  };

  const handleTransferPM = async () => {
    if (!memberToPromote) return;
    setIsTransferring(true);
    try {
      await api.post(`/projects/${activeProjectId}/transfer-pm`, {
        newOwnerEmail: memberToPromote.email
      });
      setSuccessModal({
        show: true,
        title: t('transferSuccess'),
        message: t('transferSuccessDesc')
      });
      setShowTransferModal(false);
      setMemberToPromote(null);
      await refreshProjects();
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('transferError'),
        message: err.response?.data?.error || t('errorTransferringOwnership')
      });
    } finally {
      setIsTransferring(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove || !activeProjectId) return;
    setIsRemovingMember(true);
    try {
      await api.delete(`/projects/${activeProjectId}/members/${memberToRemove.email}`);
      setSuccessModal({
        show: true,
        title: t('removeMember'),
        message: t('toastMemberRemoved')
      });
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
      await refreshProjects();
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to remove member'
      });
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleCancelInvitation = async () => {
    if (!inviteToCancel || !activeProjectId) return;
    setIsCancellingInvite(true);
    try {
      await api.delete(`/projects/${activeProjectId}/invitations/${inviteToCancel._id}`);
      setSuccessModal({
        show: true,
        title: t('cancelInvitation'),
        message: t('toastInvitationCancelled')
      });
      setShowCancelInviteModal(false);
      setInviteToCancel(null);
      fetchInvitations(activeProjectId);
    } catch (err) {
      setErrorModal({
        show: true,
        title: t('error'),
        message: err.response?.data?.error || 'Failed to cancel invitation'
      });
    } finally {
      setIsCancellingInvite(false);
    }
  };

  // Optimized Loading UI with Skeletons
  if (isLoadingProjects && workspaces.length === 0) {
    return (
      <div className="flex flex-col h-full pb-12 animate-in fade-in duration-500">
        <header className="mb-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-3">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-6 w-96" />
          </div>
        </header>
        <div className="mb-12">
          <WorkspaceSwitcherSkeleton />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 space-y-4">
            <Skeleton className="h-4 w-32 ml-2" />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
            <ProjectCardSkeleton />
          </div>
          <div className="lg:col-span-8">
            <ProjectDetailSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full pb-12">
      <header className="mb-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-display font-black text-slate-100 flex items-center gap-4">
            <Layout className="text-primary" size={32} /> {t('projects')}
          </h1>
          <p className="text-slate-500 mt-2 text-lg">{t('projectsHeaderDesc')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateWorkspaceModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-white/5 text-slate-300 border border-white/10 rounded-xl font-bold hover:bg-white/10 transition-all active:scale-95"
          >
            <Building2 size={20} /> {t('create_workspace')}
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 active:scale-95"
          >
            <Plus size={20} /> {t('createProject')}
          </button>
        </div>
      </header>

      {/* Workspace Switcher */}
      <div className="mb-12">
        {(!isInitialized && workspaces.length === 0) ? (
          <WorkspaceSwitcherSkeleton />
        ) : (
          <div className="flex items-center gap-3 overflow-x-auto pb-4 scrollbar-hide">
            {workspaces.map(ws => (
              <div key={ws._id} className="relative group/ws">
                <button
                  onClick={() => setActiveWorkspaceId(ws._id)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-2xl border transition-all whitespace-nowrap overflow-hidden relative group/ws-btn ${
                    ws.isFoundersTeam 
                      ? (activeWorkspaceId === ws._id 
                          ? 'bg-gradient-to-br from-amber-200 via-yellow-500 to-amber-600 text-slate-900 border-amber-400 shadow-[0_0_25px_rgba(245,158,11,0.3)]' 
                          : 'bg-amber-500/5 text-amber-200/50 border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40')
                      : (activeWorkspaceId === ws._id
                          ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                          : 'bg-white/5 text-slate-400 border-white/5 hover:border-white/10 hover:bg-white/10')
                  }`}
                >
                  {/* Decorative Founders Glow */}
                  {ws.isFoundersTeam && (
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(251,191,36,0.15),transparent_70%)] opacity-0 group-hover/ws-btn:opacity-100 transition-opacity" />
                  )}

                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                    ws.isFoundersTeam 
                      ? (activeWorkspaceId === ws._id ? 'bg-slate-900/20 text-slate-900' : 'bg-amber-500/10 text-amber-500 group-hover/ws-btn:scale-110')
                      : (activeWorkspaceId === ws._id ? 'bg-white/20' : 'bg-white/5 group-hover/ws-btn:scale-110')
                  }`}>
                    <Building2 size={16} />
                  </div>
                  <div className="text-left pr-4 relative z-10">
                    <span className={`block text-sm font-bold leading-none mb-1 ${
                      ws.isFoundersTeam && activeWorkspaceId === ws._id ? 'text-slate-900' : ''
                    }`}>
                      {ws.name}
                    </span>
                    <span className={`text-[10px] font-black uppercase tracking-widest ${
                      ws.isFoundersTeam 
                        ? (activeWorkspaceId === ws._id ? 'text-slate-900/60' : 'text-amber-500/40')
                        : (activeWorkspaceId === ws._id ? 'text-white/60' : 'text-slate-600')
                    }`}>
                      {ws.isFoundersTeam ? t('plan_founders') : ws.subscription?.tier === 'pro' ? t('plan_pro') : ws.subscription?.tier === 'enterprise' ? t('plan_enterprise') : t('plan_free')}
                    </span>
                  </div>
                </button>
                
                {/* Delete Workspace Button (Owner Only) */}
                {ws.ownerEmail?.toLowerCase() === user?.email?.toLowerCase() && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setWorkspaceToDelete(ws);
                      setWorkspaceDeleteInput('');
                      setShowDeleteWorkspaceModal(true);
                    }}
                    className={`absolute -top-1 -right-1 p-1.5 rounded-lg border bg-slate-900 shadow-lg opacity-0 group-hover/ws:opacity-100 transition-all z-20 ${
                      activeWorkspaceId === ws._id ? 'border-primary/20 text-white/40 hover:text-red-400' : 'border-white/10 text-slate-500 hover:text-red-500'
                    }`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {filteredProjects.length === 0 && !isLoadingProjects ? (
          <div className="lg:col-span-12 flex flex-col items-center justify-center py-20 text-center animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <div className="w-24 h-24 rounded-[32px] bg-primary/10 flex items-center justify-center text-primary mb-8 shadow-[0_0_50px_rgba(37,99,235,0.1)]">
              <Layout size={48} strokeWidth={1.5} />
            </div>
            <h2 className="text-4xl font-display font-black text-slate-100 mb-4">{t('noProjectsTitle')}</h2>
            <p className="text-slate-500 text-lg max-w-md mx-auto mb-10 leading-relaxed">
              {t('noProjectsDesc')}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/30 hover:scale-105 active:scale-95"
            >
              <Plus size={20} /> {t('createFirstProject')}
            </button>
          </div>
        ) : (
          <>
            <div className="lg:col-span-4 space-y-4">
              <h2 className="text-xs font-black uppercase tracking-widest text-slate-500 px-2 py-1 flex items-center gap-2">
                <Briefcase size={14} /> {t('availableProjects')}
              </h2>
              <div className="space-y-3">
                {isLoadingProjects && projects.length === 0 ? (
                  <>
                    <ProjectCardSkeleton />
                    <ProjectCardSkeleton />
                    <ProjectCardSkeleton />
                  </>
                ) : (
                  <>
                    {projects
                      .filter(p => !activeWorkspaceId || p.workspaceId === activeWorkspaceId)
                      .map(p => (
                        <ProjectCard
                          key={p._id}
                          project={p}
                          onSelect={handleProjectSelect}
                          active={activeProjectId === p._id}
                          t={t}
                          userEmail={user?.email}
                        />
                      ))}
                    {projects.filter(p => !activeWorkspaceId || p.workspaceId === activeWorkspaceId).length === 0 && (
                      <div className="p-8 rounded-2xl border border-dashed border-white/5 text-center">
                        <p className="text-slate-500 text-sm">{t('noResultsFound')}</p>
                        <button
                          onClick={() => setShowCreateModal(true)}
                          className="text-primary text-xs font-bold mt-2 hover:underline"
                        >
                          + {t('createProject')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="lg:col-span-8">
              <AnimatePresence mode="wait">
                {activeProject ? (
                  <motion.div
                    key={activeProject._id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-8"
                  >
                    <div className="glass-card p-8 border-white/5 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                      <div className="flex items-start justify-between relative z-10">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest border border-primary/20">{t('activeProject')}</span>
                          </div>
                          <h2 className="text-3xl font-display font-bold text-slate-100">{activeProject.name}</h2>
                          <p className="text-slate-500 mt-2">{t('projectId')}: <code className="text-xs text-primary/70">{activeProject._id}</code></p>
                        </div>
                        <div className="flex gap-2">
                          {userRole === 'pm' && (
                            <button
                              onClick={() => handleDeleteAttempt(activeProject)}
                              className="p-3.5 rounded-2xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all shadow-lg shadow-rose-500/5 active:scale-95"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Unified Access Control Section */}
                    <div className="space-y-6 mt-12 mb-16">
                      <div className="flex items-center justify-between">
                        <h3 className="text-2xl font-black text-white italic flex items-center gap-3">
                          <ShieldCheck className="text-primary" size={28} /> {t('access_section')}
                        </h3>
                      </div>

                      <div className="glass-panel p-1 rounded-[1rem] bg-white/5 border border-white/10 flex items-center gap-1 mb-8 relative">
                        <div className="flex items-center gap-1 w-full relative z-10 transition-all p-[1px]">
                          {[
                            { id: 'api', label: t('tab_api'), icon: Key, color: 'bg-primary', shadow: 'shadow-[0_0_20px_rgba(37,99,235,0.3)]', iconColor: 'text-primary' },
                            { id: 'weekly', label: t('tab_weekly'), icon: RefreshCw, color: 'bg-amber-500', shadow: 'shadow-[0_0_20px_rgba(245,158,11,0.3)]', iconColor: 'text-amber-500' },
                            { id: 'personal', label: t('tab_personal'), icon: UserPlus, color: 'bg-emerald-500', shadow: 'shadow-[0_0_20px_rgba(16,185,129,0.3)]', iconColor: 'text-emerald-500' }
                          ].map(tab => (
                            <button
                              key={tab.id}
                              onClick={() => setActiveAccessTab(tab.id)}
                              className={`flex-1 min-w-[140px] py-3.5 rounded-2xl font-bold text-[10px] uppercase tracking-[0.2em] transition-all relative group ${activeAccessTab === tab.id ? 'text-white' : 'text-slate-500 hover:text-slate-300'
                                }`}
                            >
                              {activeAccessTab === tab.id && (
                                <motion.div
                                  layoutId="activeTabIndicator"
                                  className={`absolute inset-0 ${tab.color} ${tab.shadow} rounded-[0.875rem] z-0`}
                                  transition={{ type: "tween", ease: "circOut", duration: 0.3 }}
                                />
                              )}
                              <span className="relative z-10 flex items-center justify-center gap-2">
                                <tab.icon size={14} className={activeAccessTab === tab.id ? 'text-white' : `${tab.iconColor}/40 group-hover:${tab.iconColor}`} />
                                {tab.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeAccessTab}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                          className="min-h-[300px] overflow-hidden"
                        >
                          {activeAccessTab === 'api' && (
                            <div className="space-y-6">
                              <div className="flex flex-col">
                                <h4 className="text-lg font-bold text-slate-100">{t('apiKeys')}</h4>
                                <p className="text-sm text-slate-500 mt-1 leading-relaxed max-w-2xl">
                                  {t('apiAccessKeysDesc')}
                                </p>
                              </div>

                              <div className="glass-card p-10 border-primary/10 bg-primary/[0.02] relative overflow-hidden group/api">
                                <div className="relative z-10">
                                  <div className="space-y-6">
                                    {keys.length === 0 ? (
                                      <div className="text-center py-6">
                                        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-primary/20">
                                          <Key size={32} className="text-primary" />
                                        </div>
                                        <p className="text-slate-500 font-medium">{t('noApiKeys')}</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-6">
                                        {keys.map(key => {
                                          const isCreator = key.createdByEmail?.toLowerCase() === user?.email?.toLowerCase();
                                          const canDelete = userRole === 'pm' || isCreator;
                                          const isLegacy = key._id === 'legacy_key' || key.isLegacy;
                                          const finalCanDelete = isLegacy ? userRole === 'pm' : canDelete;

                                          return (
                                            <div key={key._id} className="animate-in fade-in slide-in-from-top-4 duration-500">
                                              <div className="flex flex-col gap-2">
                                                <div className="flex items-center justify-between px-1">
                                                  <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">
                                                        {key.name === 'Default Project Key' ? t('defaultProjectKey') : key.name}
                                                      </span>
                                                      {isCreator && !isLegacy && (
                                                        <span className="text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest border border-primary/20">{t('yours')}</span>
                                                      )}
                                                    </div>
                                                    <div className="flex items-center gap-3 ml-1">
                                                      {key.allowedOrigins?.map(o => (
                                                        <span key={o} className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                                                          <Globe size={12} className="text-slate-600" /> {o || t('allOrigins')}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  </div>
                                                  {userRole === 'pm' && (
                                                    <div className="flex gap-2">
                                                      <button 
                                                        onClick={() => {
                                                          setSelectedKeyForOrigins(key);
                                                          setIsOriginsModalOpen(true);
                                                        }}
                                                        className="p-2 rounded-lg bg-emerald-500/5 text-emerald-500/40 hover:bg-emerald-500 hover:text-white transition-all active:scale-90 border border-emerald-500/10"
                                                        title={t('manageOrigins')}
                                                      >
                                                        <Globe size={14} />
                                                      </button>
                                                      {finalCanDelete && (
                                                        <button
                                                          onClick={() => { setKeyToDelete(key); setShowDeleteKeyModal(true); }}
                                                          className="p-2 rounded-lg bg-rose-500/5 text-rose-500/40 hover:bg-rose-500 hover:text-white transition-all active:scale-90 border border-rose-500/10"
                                                          title={t('deleteKey')}
                                                        >
                                                          <Trash2 size={14} />
                                                        </button>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="bg-black/60 backdrop-blur-xl rounded-2xl p-5 border border-primary/20 group/code relative w-full lg:max-w-2xl shadow-inner">
                                                  <code className="text-xs text-primary/80 font-mono tracking-[0.1em] break-all leading-relaxed pr-12 block py-1">
                                                    {key.keyPrefix}********************************{key.key ? key.key.substring(key.key.length - 8) : ''}
                                                  </code>
                                                  <div className="absolute inset-y-0 right-4 flex items-center">
                                                    <CopyButton
                                                      text={key.key || key.keyPrefix}
                                                      className="text-primary hover:bg-primary/10 p-2 rounded-xl transition-all active:scale-90"
                                                      activeColor="text-primary"
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {(keys.length === 0 || keys.length < 1) && (
                                      <div className={keys.length === 0 ? "flex justify-center" : "flex items-center gap-4"}>
                                        <button
                                          onClick={() => setShowKeyModal(true)}
                                          className={`flex items-center gap-2 px-8 py-3.5 rounded-2xl font-bold transition-all active:scale-95 ${keys.length === 0
                                              ? "bg-primary text-white shadow-[0_10px_30px_rgba(37,99,235,0.3)] hover:bg-blue-600 uppercase tracking-widest text-xs"
                                              : "bg-primary/10 text-primary border border-primary/20 hover:bg-primary hover:text-white text-sm"
                                            }`}
                                        >
                                          <Plus size={18} /> {t('newApiKey')}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {activeAccessTab === 'weekly' && (
                            <div className="space-y-6">
                              <div className="flex flex-col">
                                <h4 className="text-lg font-bold text-slate-100">{t('weeklyAccessKey')}</h4>
                                <p className="text-sm text-slate-500 mt-1 leading-relaxed max-w-2xl">
                                  {t('weeklyKeyDesc')}
                                </p>
                              </div>

                              <div className="glass-card p-10 border-amber-500/10 bg-amber-500/[0.02] relative overflow-hidden group/weekly">
                                <div className="relative z-10 flex flex-col gap-6">
                                  {!weeklyKey ? (
                                    <div className="text-center py-6">
                                      <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-500/20">
                                        <RefreshCw size={32} className="text-amber-500" />
                                      </div>
                                      <p className="text-slate-500 font-medium mb-8">{t('noWeeklyKey')}</p>
                                      <button
                                        onClick={handleGenerateWeeklyKey}
                                        disabled={isGeneratingWeeklyKey}
                                        className="px-8 py-3.5 bg-amber-500 text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-amber-600 transition-all shadow-[0_10px_30px_rgba(245,158,11,0.2)] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                      >
                                        {isGeneratingWeeklyKey ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                                        {t('generateWeeklyKey')}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="space-y-6">
                                      <div className="flex items-center justify-between px-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-black uppercase tracking-widest text-amber-500/60 ml-1">
                                            {t('activeKey')}
                                          </span>
                                          <span className="text-[8px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest border border-amber-500/20">{t('yours')}</span>
                                        </div>
                                        <button
                                          onClick={handleGenerateWeeklyKey}
                                          disabled={isGeneratingWeeklyKey}
                                          className="text-[10px] font-black uppercase tracking-widest text-amber-500 hover:text-amber-400 transition-all flex items-center gap-2"
                                        >
                                          {isGeneratingWeeklyKey ? <RefreshCw className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                                          {t('regenerateWeeklyKey')}
                                        </button>
                                      </div>

                                      <div className="bg-black/60 backdrop-blur-xl rounded-2xl p-5 border border-amber-500/20 group/code relative w-full lg:max-w-2xl shadow-inner">
                                        <code className="text-xs text-amber-500/80 font-mono tracking-[0.1em] break-all leading-relaxed pr-12 block py-1">
                                          {weeklyKey}
                                        </code>
                                        <div className="absolute inset-y-0 right-4 flex items-center">
                                          <CopyButton
                                            text={weeklyKey}
                                            className="text-amber-500 hover:bg-amber-500/10 p-2 rounded-xl transition-all active:scale-90"
                                            activeColor="text-amber-500"
                                          />
                                        </div>
                                      </div>
                                      
                                      <p className="text-[10px] text-slate-600 italic ml-1">
                                        {activeProject?.currentUserWeeklyKeyExpiresAt 
                                          ? t('weeklyKeyExpiresIn', { days: calculateDaysLeft(activeProject.currentUserWeeklyKeyExpiresAt) }) 
                                          : t('weeklyKeyWarning')}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {activeAccessTab === 'personal' && (
                            <div className="space-y-6">
                              <div className="flex flex-col">
                                <h4 className="text-lg font-bold text-slate-100">{t('personalKey')}</h4>
                                <p className="text-sm text-slate-500 mt-1 leading-relaxed max-w-2xl">
                                  {t('personalKeyDesc')}
                                </p>
                              </div>

                              <div className="glass-card p-10 border-emerald-500/10 bg-emerald-500/[0.02] relative overflow-hidden group/personal">
                                <div className="relative z-10 flex flex-col gap-6">
                                  {!user?.individualApiKey ? (
                                    <div className="text-center py-6">
                                      <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20">
                                        <UserPlus size={32} className="text-emerald-500" />
                                      </div>
                                      <p className="text-slate-500 font-medium mb-8">{t('noPersonalKey')}</p>
                                      <button
                                        onClick={handleGeneratePersonalKey}
                                        disabled={isGeneratingPersonalKey}
                                        className="px-8 py-3.5 bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-emerald-600 transition-all shadow-[0_10px_30px_rgba(16,185,129,0.2)] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                                      >
                                        {isGeneratingPersonalKey ? <RefreshCw className="animate-spin" size={16} /> : <UserPlus size={16} />}
                                        {t('generatePersonalKey')}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="space-y-6">
                                      <div className="flex items-center justify-between px-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500/60 ml-1">
                                             {t('globalUserKey')}
                                          </span>
                                          <span className="text-[8px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest border border-emerald-500/20">PRIVATE</span>
                                        </div>
                                        <button
                                          onClick={handleGeneratePersonalKey}
                                          disabled={isGeneratingPersonalKey}
                                          className="text-[10px] font-black uppercase tracking-widest text-emerald-500 hover:text-amber-400 transition-all flex items-center gap-2"
                                        >
                                          {isGeneratingPersonalKey ? <RefreshCw className="animate-spin" size={12} /> : <RefreshCw size={12} />}
                                          {t('regeneratePersonalKey')}
                                        </button>
                                      </div>

                                      <div className="bg-black/60 backdrop-blur-xl rounded-2xl p-5 border border-emerald-500/20 group/code relative w-full lg:max-w-2xl shadow-inner">
                                        <code className="text-xs text-emerald-500/80 font-mono tracking-[0.1em] break-all leading-relaxed pr-12 block py-1">
                                          {user.individualApiKey}
                                        </code>
                                        <div className="absolute inset-y-0 right-4 flex items-center">
                                          <CopyButton
                                            text={user.individualApiKey}
                                            className="text-emerald-500 hover:bg-emerald-500/10 p-2 rounded-xl transition-all active:scale-90"
                                            activeColor="text-emerald-500"
                                          />
                                        </div>
                                      </div>
                                      
                                      <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl flex gap-3 items-start max-w-2xl">
                                         <ShieldCheck className="text-emerald-500 mt-0.5" size={16} />
                                         <p className="text-[11px] text-slate-500 leading-relaxed">
                                           {t('personalKeyWarning')}
                                         </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Team & Members Section */}
                    <div className="space-y-8 mt-20 pb-32 animate-in fade-in duration-1000 slide-in-from-bottom-8">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <h3 className="text-2xl font-black text-white italic flex items-center gap-3">
                            <UserPlus className="text-primary" size={28} /> {t('members')}
                          </h3>
                          <p className="text-slate-500 text-sm italic">{t('teamManagementDesc')}</p>
                        </div>
                        {userRole === 'pm' && (
                          <button
                            onClick={() => setIsInviteModalOpen(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 active:scale-95"
                          >
                            <PlusCircle size={20} /> {t('addMember')}
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(() => {
                           const projectMembers = activeProject.members || [];
                           const ownerEmail = activeProject.ownerEmail || activeProject.owner_email;
                           
                           // Create a combined list starting with owner if not already in members
                           const allMembers = [...projectMembers];
                           if (ownerEmail && !allMembers.some(m => m.email?.toLowerCase() === ownerEmail.toLowerCase())) {
                             allMembers.unshift({ email: ownerEmail, role: 'pm' });
                           }

                           return allMembers.map(member => {
                           const isMe = member.email?.toLowerCase() === user?.email?.toLowerCase();
                           const isPM = member.role === 'pm';
                           
                           return (
                             <div key={member.email} className="glass-panel rounded-[1rem] p-6 border-white/5 bg-white/[0.02] hover:border-white/10 transition-all flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                   <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl font-bold ${
                                     isMe ? 'bg-primary text-white' : 'bg-white/5 text-slate-400'
                                   }`}>
                                     {member.email?.charAt(0).toUpperCase()}
                                   </div>
                                   <div>
                                     <div className="flex items-center gap-2">
                                       <h4 className="font-bold text-slate-100">{member.email}</h4>
                                       {isMe && <span className="text-[8px] bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest">{t('yours')}</span>}
                                     </div>
                                     <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mt-0.5">
                                       {isPM ? <ShieldCheck size={12} className="text-amber-500" /> : <Code2 size={12} />}
                                       {t(member.role?.toLowerCase().startsWith('role_') ? member.role.toLowerCase() : `role_${member.role}`)}
                                     </span>
                                   </div>
                                </div>
                                
                                {userRole === 'pm' && !isMe && (
                                  <div className="flex items-center gap-2">
                                     <button 
                                       onClick={() => {
                                         setMemberToPromote(member);
                                         setShowTransferModal(true);
                                       }}
                                       className="p-2.5 rounded-xl hover:bg-amber-500/10 text-slate-500 hover:text-amber-500 transition-all"
                                       title={t('designateAsPM')}
                                     >
                                       <ShieldCheck size={18} />
                                     </button>
                                     <button 
                                       onClick={() => {
                                         setMemberToRemove(member);
                                         setShowRemoveMemberModal(true);
                                       }}
                                       className="p-2.5 rounded-xl hover:bg-rose-500/10 text-slate-500 hover:text-rose-500 transition-all"
                                       title={t('removeMember')}
                                     >
                                       <Trash2 size={18} />
                                     </button>
                                  </div>
                                )}
                             </div>
                           );
                         })})()}

                        {/* Pending Invitations */}
                        {pendingInvitations.map(invitation => (
                           <div key={invitation._id} className="glass-panel rounded-[1rem] p-6 border-dashed border-primary/20 bg-primary/[0.02] flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                 <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-slate-500 border border-dashed border-white/10">
                                   <Mail size={20} />
                                 </div>
                                 <div>
                                   <div className="flex items-center gap-2">
                                     <h4 className="font-bold text-slate-300">{invitation.email}</h4>
                                     <span className="text-[8px] bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest">{t('pending')}</span>
                                   </div>
                                   <span className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center gap-1.5 mt-0.5">
                                     {t(invitation.role?.toLowerCase().startsWith('role_') ? invitation.role.toLowerCase() : `role_${invitation.role}`)}
                                   </span>
                                 </div>
                              </div>
                              
                              {userRole === 'pm' && (
                                <button 
                                  onClick={() => {
                                    setInviteToCancel(invitation);
                                    setShowCancelInviteModal(true);
                                  }}
                                  className="p-2.5 rounded-xl hover:bg-rose-500/10 text-slate-500 hover:text-rose-500 transition-all"
                                  title={t('cancelInvitation')}
                                >
                                  <XCircle size={18} />
                                </button>
                              )}
                           </div>
                        ))}

                        {userRole === 'pm' && pendingInvitations.length === 0 && (activeProject.members?.length || 0) === 0 && !activeProject.ownerEmail && (
                           <button 
                             onClick={() => setIsInviteModalOpen(true)}
                             className="md:col-span-2 group border-2 border-dashed border-white/5 rounded-[2rem] p-10 flex flex-col items-center justify-center gap-4 hover:border-primary/30 hover:bg-primary/[0.02] transition-all"
                           >
                              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center text-slate-500 group-hover:bg-primary/[0.05] group-hover:text-primary transition-all">
                                 <UserPlus size={32} />
                              </div>
                              <p className="text-slate-500 group-hover:text-slate-300 font-medium transition-all">{t('noMembers')}</p>
                           </button>
                        )}
                      </div>
                    </div>
                  </motion.div>

                ) : (
                  <div className="h-full flex flex-col items-center justify-center p-20 text-slate-600 space-y-4">
                    <Briefcase size={80} strokeWidth={1} />
                    <p className="font-medium">{t('noProjectsSelected')}</p>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>

      {/* MODALS */}
 
      {/* Create Workspace Modal */}
      {createPortal(
        <AnimatePresence>
          {showCreateWorkspaceModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowCreateWorkspaceModal(false)}
                className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden"
              >
                <div className="p-8 border-b border-white/5 bg-white/[0.02]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                      <Building2 size={24} />
                    </div>
                    <button onClick={() => setShowCreateWorkspaceModal(false)} className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-all">
                      <X size={20} />
                    </button>
                  </div>
                  <h2 className="text-2xl font-black text-slate-100">{t('create_workspace')}</h2>
                  <p className="text-slate-500 text-sm mt-1">{t('workspace_desc')}</p>
                </div>
 
                <form onSubmit={handleCreateWorkspace} className="p-8 space-y-6">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2 ml-1">{t('workspace_name')}</label>
                    <input
                      autoFocus
                      type="text"
                      required
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-slate-100 focus:outline-none focus:border-primary/50 transition-all placeholder:text-slate-700 font-medium"
                      placeholder={t('workspace_placeholder')}
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-4">
                    <button type="button" onClick={() => setShowCreateWorkspaceModal(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white/5 transition-all">
                      {t('cancel')}
                    </button>
                    <button type="submit" className="flex-1 px-6 py-4 bg-primary text-white rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 active:scale-95">
                      {t('create_workspace')}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Create Project Modal */}
      {createPortal(
        <AnimatePresence>
          {showCreateModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowCreateModal(false)}
                className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden"
              >
                <div className="p-8 border-b border-white/5 bg-white/[0.02]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                      <Plus size={24} />
                    </div>
                    <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-all">
                      <X size={20} />
                    </button>
                  </div>
                  <h2 className="text-2xl font-black text-slate-100">{t('createProject')}</h2>
                </div>
                <form onSubmit={handleCreateProject} className="p-8 space-y-6">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2 ml-1">{t('projectName')}</label>
                    <input
                      autoFocus
                      type="text"
                      required
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-slate-100 focus:outline-none focus:border-primary/50 transition-all placeholder:text-slate-700 font-medium"
                    />
                  </div>
                  <div className="px-4 py-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                    <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest flex items-center gap-2">
                       <Building2 size={12} /> {t('activeWorkspace')}: {workspaces.find(w => w._id === activeWorkspaceId)?.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white/5 transition-all">
                      {t('cancel')}
                    </button>
                    <button type="submit" className="flex-1 px-6 py-4 bg-primary text-white rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 active:scale-95">
                      {t('createProject')}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Portals for Global Modals */}
      {createPortal(
        <AnimatePresence>
          {showDeleteModal && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowDeleteModal(false)}
                className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
              />
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="relative bg-slate-900 w-full max-w-md rounded-[2.5rem] border border-rose-500/30 overflow-hidden shadow-2xl"
              >
                <div className="p-8 bg-rose-500/5 border-b border-rose-500/10 text-center">
                  <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-500">
                    <Trash2 size={32} />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-2">{t('deleteProject')}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t('deleteProjectConfirm')}
                  </p>
                  <div className="mt-4 p-3 bg-rose-500/10 rounded-xl border border-rose-500/20">
                    <p className="text-[10px] text-rose-400 font-bold uppercase tracking-widest">{projectToDelete?.name}</p>
                  </div>
                </div>
                <div className="p-8 space-y-4">
                  <div className="flex gap-3">
                    <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">{t('cancel')}</button>
                    <button onClick={confirmDeleteProject} className="flex-1 py-4 bg-rose-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-rose-500/20 active:scale-95 transition-all">
                      {t('delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Portals for other modals (Delete, Error, Success) should be kept from original or added here */}
      {createPortal(
        <AnimatePresence>
          {successModal.show && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setSuccessModal({ ...successModal, show: false })} />
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative bg-slate-900 p-8 rounded-[2.5rem] border border-emerald-500/20 text-center max-w-sm shadow-2xl">
                <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-500"><CheckCircle2 size={32} /></div>
                <h3 className="text-xl font-bold text-white mb-2">{successModal.title}</h3>
                <p className="text-slate-400 text-sm mb-6">{successModal.message}</p>
                <button onClick={() => setSuccessModal({ ...successModal, show: false })} className="w-full py-4 bg-emerald-500 text-white rounded-2xl font-bold">OK</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {errorModal.show && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setErrorModal({ ...errorModal, show: false })} />
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative bg-slate-900 p-8 rounded-[2.5rem] border border-rose-500/20 text-center max-w-sm shadow-2xl">
                <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-6 text-rose-500"><AlertTriangle size={32} /></div>
                <h3 className="text-xl font-bold text-white mb-2">{errorModal.title}</h3>
                <p className="text-slate-400 text-sm mb-6">{errorModal.message}</p>
                <button onClick={() => setErrorModal({ ...errorModal, show: false })} className="w-full py-4 bg-rose-500 text-white rounded-2xl font-bold">OK</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {showDeleteWorkspaceModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => !isDeletingWorkspace && setShowDeleteWorkspaceModal(false)} />
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative bg-slate-900 w-full max-w-md rounded-[2.5rem] border border-rose-500/30 overflow-hidden shadow-2xl">
                <div className="p-8 bg-rose-500/5 border-b border-rose-500/10">
                  <div className="w-16 h-16 bg-rose-500/10 rounded-3xl flex items-center justify-center text-rose-500 mb-6 mx-auto">
                    <AlertTriangle size={32} />
                  </div>
                  <h3 className="text-2xl font-black text-white text-center mb-2">{t('deleteWorkspace')}?</h3>
                  <p className="text-slate-400 text-center text-sm leading-relaxed">
                    {t('deleteWorkspaceConfirm', { irreversible: t('irreversible'), name: workspaceToDelete?.name })}
                  </p>
                </div>
                
                <div className="p-8 space-y-6">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 ml-1">
                      {t('confirmType', { name: workspaceToDelete?.name })}:
                    </label>
                    <input
                      type="text"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-slate-100 focus:outline-none focus:border-rose-500/50 transition-all font-medium"
                      placeholder={workspaceToDelete?.name}
                      value={workspaceDeleteInput}
                      onChange={(e) => setWorkspaceDeleteInput(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      disabled={isDeletingWorkspace}
                      onClick={() => setShowDeleteWorkspaceModal(false)}
                      className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      disabled={isDeletingWorkspace || workspaceDeleteInput !== workspaceToDelete?.name}
                      onClick={handleDeleteWorkspace}
                      className="flex-1 py-4 bg-rose-500 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-rose-500/20 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      {isDeletingWorkspace ? <RefreshCw className="animate-spin" size={16} /> : <Trash2 size={16} />}
                      {isDeletingWorkspace ? t('deleting') : t('delete')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Invite Member Modal */}
      {createPortal(
        <AnimatePresence>
          {isInviteModalOpen && activeProject && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsInviteModalOpen(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
              <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden">
                <div className="p-8 border-b border-white/5 bg-white/[0.02]">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-bold">
                      <UserPlus size={24} />
                    </div>
                    <button onClick={() => setIsInviteModalOpen(false)} className="p-2 hover:bg-white/5 rounded-xl text-slate-500 transition-all">
                      <X size={20} />
                    </button>
                  </div>
                  <h2 className="text-2xl font-black text-slate-100">{t('addMember')}</h2>
                  <p className="text-slate-500 text-sm mt-1">{t('inviteDesc').replace('{name}', activeProject.name)}</p>
                </div>
                <form onSubmit={handleInvite} className="p-8 space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2 ml-1">{t('emailAddress')}</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
                        <input
                          autoFocus
                          type="email"
                          required
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="w-full bg-black/40 border border-white/10 rounded-2xl pl-12 pr-6 py-4 text-slate-100 focus:outline-none focus:border-primary/50 transition-all placeholder:text-slate-700 font-medium"
                          placeholder={t('emailPlaceholder')}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-2 ml-1">{t('role')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['dev', 'ux_ui', 'pm', 'qa', 'client'].map(r => {
                          const isPmDisabled = r === 'pm' && pmCountTotal >= 2;
                          return (
                            <button
                              key={r}
                              type="button"
                              onClick={() => !isPmDisabled && setInviteRole(r)}
                              disabled={isPmDisabled}
                              className={`px-4 py-3 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all relative ${
                                inviteRole === r ? 'bg-primary/10 border-primary text-primary' : 'bg-white/5 border-white/5 text-slate-500 hover:border-white/10'
                              } ${isPmDisabled ? 'opacity-30 cursor-not-allowed grayscale' : ''}`}
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span>{t(r.toLowerCase().startsWith('role_') ? r.toLowerCase() : `role_${r.toLowerCase()}`)}</span>
                                {isPmDisabled && <span className="text-[8px] text-rose-500/80 font-bold mt-1 leading-none">{t('pmLimitReachedShort')}</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    <button type="button" onClick={() => setIsInviteModalOpen(false)} className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white/5 transition-all">
                      {t('cancel')}
                    </button>
                    <button type="submit" disabled={isInviting || !inviteEmail} className="flex-1 px-6 py-4 bg-primary text-white rounded-2xl font-bold hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 active:scale-95 disabled:opacity-50">
                      {isInviting ? <RefreshCw className="animate-spin mx-auto" size={20} /> : t('sendInvitation')}
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Delete Key Confirmation Modal */}
      {createPortal(
        <AnimatePresence>
          {showDeleteKeyModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowDeleteKeyModal(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="relative bg-slate-900 p-8 rounded-[2.5rem] border border-rose-500/20 text-center max-w-sm overflow-hidden shadow-2xl">
                 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />
                 <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-500 shadow-lg shadow-rose-500/5">
                   <AlertTriangle size={32} />
                 </div>
                 <h3 className="text-2xl font-black text-white mb-2">{t('deleteKey')}</h3>
                 <p className="text-slate-400 text-sm mb-8 leading-relaxed">{t('deleteKeyConfirm')}</p>
                 <div className="flex gap-3">
                   <button onClick={() => setShowDeleteKeyModal(false)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">{t('cancel')}</button>
                   <button onClick={handleDeleteKey} disabled={isDeletingKey} className="flex-1 py-4 bg-rose-500 text-white rounded-[2.5rem] font-bold active:scale-95 transition-all shadow-lg shadow-rose-500/20">
                     {isDeletingKey ? <RefreshCw className="animate-spin mx-auto" size={20} /> : t('delete')}
                   </button>
                 </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Remove Member Confirmation Modal */}
      {createPortal(
        <AnimatePresence>
          {showRemoveMemberModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowRemoveMemberModal(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="relative bg-slate-900 p-8 rounded-[2.5rem] border border-rose-500/20 text-center max-w-sm shadow-2xl">
                 <div className="w-20 h-20 bg-rose-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 text-rose-500">
                   <UserPlus size={32} className="rotate-45" />
                 </div>
                 <h3 className="text-2xl font-black text-white mb-2">{t('removeMember')}</h3>
                 <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                   {t('confirmRemoveMember')}
                   <br />
                   <span className="text-rose-400/80 font-bold mt-2 block">{memberToRemove?.email}</span>
                 </p>
                 <div className="flex gap-3">
                   <button onClick={() => setShowRemoveMemberModal(false)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">{t('cancel')}</button>
                   <button onClick={handleRemoveMember} disabled={isRemovingMember} className="flex-1 py-4 bg-rose-500 text-white rounded-[2.5rem] font-bold active:scale-95 transition-all">
                     {isRemovingMember ? <RefreshCw className="animate-spin mx-auto" size={20} /> : t('remove')}
                   </button>
                 </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Cancel Invitation Confirmation Modal */}
      {createPortal(
        <AnimatePresence>
          {showCancelInviteModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCancelInviteModal(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="relative bg-slate-900 p-8 rounded-[2.5rem] border border-white/10 text-center max-w-sm shadow-2xl">
                 <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-6 text-slate-400">
                   <XCircle size={32} />
                 </div>
                 <h3 className="text-2xl font-black text-white mb-2">{t('cancelInvitation')}</h3>
                 <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                   {t('confirmCancelInvitation')}
                   <br />
                   <span className="text-slate-300 font-bold mt-2 block">{inviteToCancel?.email}</span>
                 </p>
                 <div className="flex gap-3">
                   <button onClick={() => setShowCancelInviteModal(false)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">{t('cancel')}</button>
                   <button onClick={handleCancelInvitation} disabled={isCancellingInvite} className="flex-1 py-4 bg-slate-100 text-slate-900 rounded-[2.5rem] font-bold active:scale-95 transition-all">
                     {isCancellingInvite ? <RefreshCw className="animate-spin mx-auto" size={20} /> : t('confirm')}
                   </button>
                 </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Transfer PM Confirmation Modal */}
      {createPortal(
        <AnimatePresence>
          {showTransferModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowTransferModal(false)} className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="relative bg-slate-900 w-full max-w-md rounded-[2.5rem] border border-amber-500/30 overflow-hidden shadow-2xl">
                <div className="p-8 bg-amber-500/5 border-b border-amber-500/10 text-center">
                  <div className="w-20 h-20 bg-amber-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 text-amber-500">
                    <ShieldCheck size={40} />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-2">{t('transferPM')}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {t('transferWarningDesc').replace('{{email}}', memberToPromote?.email)}
                  </p>
                </div>
                <div className="p-8 space-y-6">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 ml-1">
                      {t('toConfirmWrite')}: <span className="text-white">{t('confirmTransferAction')}</span>
                    </label>
                    <input
                      type="text"
                      className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 text-slate-100 focus:outline-none focus:border-amber-500/50 transition-all font-medium"
                      placeholder={t('confirmTransferAction')}
                      value={transferConfirmationInput}
                      onChange={(e) => setTransferConfirmationInput(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => setShowTransferModal(false)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all">{t('cancel')}</button>
                    <button onClick={handleTransferPM} disabled={isTransferring || transferConfirmationInput !== t('confirmTransferAction')} className="flex-1 py-4 bg-amber-500 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-[2.5rem] font-black uppercase tracking-widest text-xs shadow-lg shadow-amber-500/20 active:scale-95 transition-all">
                      {isTransferring ? <RefreshCw className="animate-spin mx-auto" size={16} /> : t('confirmTransfer')}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {createPortal(
        <AnimatePresence>
          {isOriginsModalOpen && selectedKeyForOrigins && (
            <OriginsModal 
              isOpen={isOriginsModalOpen}
              onClose={() => setIsOriginsModalOpen(false)}
              keyData={selectedKeyForOrigins}
              onUpdate={handleUpdateOrigins}
              isUpdating={isUpdatingOrigins}
              t={t}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Projects;
